import React, { useState, useEffect } from 'react';
import {
  TextField,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
  Box,
} from '@mui/material';
import { FieldMapping } from '../../services/fieldMapping.service';
import { isFieldHidden, isFieldRequired } from '../../utils/formUtils';

interface BenefitsInformationProps {
  data?: any;
  onChange?: (data: any) => void;
  formData?: any;
  onFormDataChange?: (data: any) => void;
  fieldMappings?: FieldMapping[];
  isRequired?: boolean;
  readOnly?: boolean;
}

interface FormData {
  // Social Security
  social_security: string;
  social_security_amount: string;
  social_security_frequency: string;

  // Workers Compensation
  workers_compensation: string;
  workers_compensation_amount: string;
  workers_compensation_frequency: string;

  // Long Term Disability
  long_term_disability: string;
  long_term_disability_amount: string;
  long_term_disability_frequency: string;

  // Pension
  pension: string;
  pension_amount: string;
  pension_frequency: string;

  // Accident Disability
  accident_disability: string;
  accident_disability_amount: string;
  accident_disability_frequency: string;

  // Other
  other_benefits: string;
  other_benefits_amount: string;
  other_benefits_frequency: string;
}

const BenefitsInformation: React.FC<BenefitsInformationProps> = ({
  data,
  onChange,
  formData: initialFormData,
  onFormDataChange,
  fieldMappings = [],
  readOnly = false,
}) => {
  const actualFormData = initialFormData || data || {};
  const actualOnChange =
    onFormDataChange ||
    onChange ||
    ((_data: any) => { /* noop */ });

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const isFieldHiddenLocal  = (f: string) => isFieldHidden(f, 'benefits_information', fieldMappings);
  const isFieldRequiredLocal = (f: string) => isFieldRequired(f, 'benefits_information', fieldMappings);
  const [formData, setFormData] = useState<FormData>({
    // Social Security
    social_security: '',
    social_security_amount: '',
    social_security_frequency: '',

    // Workers Compensation
    workers_compensation: '',
    workers_compensation_amount: '',
    workers_compensation_frequency: '',

    // Long Term Disability
    long_term_disability: '',
    long_term_disability_amount: '',
    long_term_disability_frequency: '',

    // Pension
    pension: '',
    pension_amount: '',
    pension_frequency: '',

    // Accident Disability
    accident_disability: '',
    accident_disability_amount: '',
    accident_disability_frequency: '',

    // Other
    other_benefits: '',
    other_benefits_amount: '',
    other_benefits_frequency: '',
    ...actualFormData,
  });

  useEffect(() => {
    setFormData((prev: FormData) => ({ ...prev, ...actualFormData }));
  }, [actualFormData]);

  const handleChange = (field: string, value: any) => {
    if (readOnly) return; // Prevent changes in read-only mode
    const updatedData = { ...formData, [field]: value };
    setFormData(updatedData);
    actualOnChange(updatedData);
  };

  const renderBenefitSection = (
    benefitType: string,
    benefitKey: string,
    amountKey: string,
    frequencyKey: string,
    title: string
  ) => (
    <Box sx={{ mb: 3 }}>
      <Typography variant='h6' gutterBottom>
        {title}
      </Typography>
      <FormControl component='fieldset' sx={{ mb: 2 }}>
        <RadioGroup
          value={formData[benefitKey as keyof FormData]}
          onChange={e => handleChange(benefitKey, e.target.value)}
        >
          <FormControlLabel value='yes' control={<Radio />} label='Yes' />
          <FormControlLabel value='no' control={<Radio />} label='No' />
        </RadioGroup>
      </FormControl>

      {formData[benefitKey as keyof FormData] === 'yes' && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              flexWrap: 'wrap',
              alignItems: 'center',
            }}
          >
            <Typography variant='subtitle1'>Amount</Typography>
            <TextField
              label='$'
              value={formData[amountKey as keyof FormData]}
              onChange={e => handleChange(amountKey, e.target.value)}
              required={isFieldRequiredLocal(amountKey)}
              sx={{ width: 150 }}
            />
          </Box>

          <FormControl component='fieldset'>
            <RadioGroup
              value={formData[frequencyKey as keyof FormData]}
              onChange={e => handleChange(frequencyKey, e.target.value)}
              sx={{ flexDirection: 'row' }}
            >
              <FormControlLabel
                value='weekly'
                control={<Radio />}
                label='Weekly'
              />
              <FormControlLabel
                value='bi_weekly'
                control={<Radio />}
                label='Bi-weekly'
              />
              <FormControlLabel
                value='monthly'
                control={<Radio />}
                label='Monthly'
              />
            </RadioGroup>
          </FormControl>
        </Box>
      )}
    </Box>
  );

  return (
    <Box>
      <Typography variant='h5' gutterBottom>
        Benefits Information
      </Typography>

      <Box sx={{ mb: 3 }}>
        <Typography variant='subtitle1' color='text.secondary'>
          Instructions: If you are receiving any benefits please select Yes ☒
          and mention the amount.
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Social Security */}
        {renderBenefitSection(
          'social_security',
          'social_security',
          'social_security_amount',
          'social_security_frequency',
          'Social Security'
        )}

        {/* Workers Compensation */}
        {renderBenefitSection(
          'workers_compensation',
          'workers_compensation',
          'workers_compensation_amount',
          'workers_compensation_frequency',
          'Workers Compensation'
        )}

        {/* Long Term Disability */}
        {renderBenefitSection(
          'long_term_disability',
          'long_term_disability',
          'long_term_disability_amount',
          'long_term_disability_frequency',
          'Long Term Disability'
        )}

        {/* Pension */}
        {renderBenefitSection(
          'pension',
          'pension',
          'pension_amount',
          'pension_frequency',
          'Pension'
        )}

        {/* Accident Disability */}
        {renderBenefitSection(
          'accident_disability',
          'accident_disability',
          'accident_disability_amount',
          'accident_disability_frequency',
          'Accident Disability'
        )}

        {/* Other */}
        {renderBenefitSection(
          'other_benefits',
          'other_benefits',
          'other_benefits_amount',
          'other_benefits_frequency',
          'Other'
        )}
      </Box>
    </Box>
  );
};

export default BenefitsInformation;
